import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, Send, Upload, Minimize2, Loader, ArrowLeft, Home, Maximize2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { aiChatService } from '../../services/aiChatService';
import growGuardianAIService from '../../services/growGuardianAIService';
import ChatWindow from './components/ChatWindow';
import ConversationSidebar from './components/ConversationSidebar';
import QuickReplies from './components/QuickReplies';
import ImageUpload from './components/ImageUpload';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';


export default function AiChatAssistant() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isMinimized, setIsMinimized] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState('');
  const [showImageUpload, setShowImageUpload] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [apiKeyError, setApiKeyError] = useState(false);
  const [failedMessage, setFailedMessage] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  const MAX_RETRIES = 3;
  const RETRY_DELAY_MS = 1500;

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const getGeminiErrorMessage = (error) => {
    const msg = error?.message || '';
    if (msg?.includes('Not authenticated') || msg?.includes('session')) {
      return {
        text: '🔒 Your session has expired. Please sign in again to continue using AI chat.',
        isAuth: true,
        retryable: false,
      };
    }
    if (msg?.includes('quota') || msg?.includes('rate') || msg?.includes('429')) {
      return {
        text: '⏳ The AI service is currently rate-limited. Please wait a moment and try again.',
        isAuth: false,
        retryable: true,
      };
    }
    if (msg?.includes('503') || msg?.includes('overloaded') || msg?.includes('unavailable')) {
      return {
        text: '🔄 The AI service is temporarily overloaded. Retrying automatically…',
        isAuth: false,
        retryable: true,
      };
    }
    if (msg?.includes('Network') || msg?.includes('fetch') || msg?.includes('Failed to fetch')) {
      return {
        text: '📡 Network connection issue. Please check your internet connection and try again.',
        isAuth: false,
        retryable: true,
      };
    }
    if (msg?.includes('Backend error') || msg?.includes('Edge Function')) {
      return {
        text: '⚠️ The AI backend is temporarily unavailable. Please try again in a few moments.',
        isAuth: false,
        retryable: true,
      };
    }
    return {
      text: '⚠️ Something went wrong while getting a response. Please try again.',
      isAuth: false,
      retryable: true,
    };
  };

  // Initialize demo mode for non-authenticated users
  useEffect(() => {
    if (!user) {
      setIsDemoMode(true);
      initializeDemoMode();
    } else {
      setIsDemoMode(false);
      loadConversations();
    }
  }, [user]);

  const initializeDemoMode = () => {
    const demoConversation = {
      id: 'demo-chat',
      title: 'Demo IPM Chat',
      conversation_status: 'active',
      created_at: new Date()?.toISOString()
    };
    
    setActiveConversation(demoConversation);
    
    // Add welcome message for demo users
    const welcomeMessage = {
      id: 'demo-welcome',
      role: 'system',
      content: '👋 Welcome to GrowGuardian AI! To use our AI-powered chat assistant with real IPM guidance, pest identification, and treatment recommendations, please sign in or create an account. AI features require authentication for security reasons.',
      created_at: new Date()?.toISOString(),
      message_type: 'system_notification'
    };
    
    setMessages([welcomeMessage]);
    
    // Add demo suggestions pointing to login
    setSuggestions([
      { id: 'demo-1', suggestion_text: 'Sign in to access AI chat', suggestion_type: 'action_button' },
      { id: 'demo-2', suggestion_text: 'Create free account', suggestion_type: 'action_button' },
      { id: 'demo-3', suggestion_text: 'Learn about AI features', suggestion_type: 'quick_reply' }
    ]);
  };

  // Load user conversations on component mount
  useEffect(() => {
    if (user) {
      loadConversations();
    }
  }, [user]);

  // Load messages when active conversation changes
  useEffect(() => {
    if (activeConversation && user) {
      loadMessages();
      loadSuggestions();
    }
  }, [activeConversation, user]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingMessage]);

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadConversations = async () => {
    try {
      const data = await aiChatService?.getConversations(user?.id);
      setConversations(data);
      
      // Auto-select first conversation or create new one if none exist
      if (data?.length > 0) {
        setActiveConversation(data?.[0]);
      } else {
        await createNewConversation();
      }
    } catch (error) {
      console.error('Failed to load conversations:', error);
    }
  };

  const loadMessages = async () => {
    if (!activeConversation) return;
    // Skip DB query for demo/non-UUID conversation IDs
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex?.test(activeConversation?.id)) return;
    
    try {
      const data = await aiChatService?.getMessages(activeConversation?.id, user?.id);
      setMessages(data);
    } catch (error) {
      console.error('Failed to load messages:', error);
    }
  };

  const loadSuggestions = async () => {
    if (!activeConversation) return;
    // Skip DB query for demo/non-UUID conversation IDs
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex?.test(activeConversation?.id)) return;
    
    try {
      const data = await aiChatService?.getSuggestions(activeConversation?.id);
      setSuggestions(data);
    } catch (error) {
      console.error('Failed to load suggestions:', error);
    }
  };

  const createNewConversation = async (title = 'IPM Guidance Chat') => {
    try {
      setIsLoading(true);
      const newConv = await aiChatService?.createConversation(user?.id, title);
      setConversations(prev => [newConv, ...prev]);
      setActiveConversation(newConv);
      return newConv;
    } catch (error) {
      console.error('Failed to create conversation:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessage = async (messageText = newMessage, messageType = 'text', isRetry = false) => {
    if (!messageText?.trim() || isLoading) return;

    // Block demo mode users from sending messages
    if (isDemoMode) {
      const infoMessage = {
        id: `demo-blocked-${Date.now()}`,
        role: 'system',
        content: '🔒 AI chat requires authentication. Please sign in to unlock full AI-powered IPM guidance, pest identification, and personalized recommendations.',
        created_at: new Date()?.toISOString(),
        message_type: 'system_notification'
      };
      setMessages(prev => [...prev, infoMessage]);
      return;
    }

    try {
      setIsLoading(true);
      if (!isRetry) {
        setNewMessage('');
        setFailedMessage(null);
        setRetryCount(0);
      }
      setApiKeyError(false);

      // Only add user message to UI on first attempt (not retries)
      if (!isRetry) {
        const userMessage = {
          id: `user-${Date.now()}`,
          role: 'user',
          content: messageText,
          message_type: messageType,
          created_at: new Date()?.toISOString()
        };
        setMessages(prev => [...prev, userMessage]);

        await aiChatService?.addUserMessage(
          activeConversation?.id,
          user?.id,
          messageText,
          messageType
        );
      }

      // Retry loop
      let lastError = null;
      for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        try {
          setIsStreaming(true);
          setStreamingMessage(attempt > 1 ? `Retrying… (attempt ${attempt}/${MAX_RETRIES})` : 'Thinking…');

          const aiResponse = await growGuardianAIService?.chatAssistant(
            messageText,
            {
              conversationId: activeConversation?.id,
              messageHistory: messages?.slice(-10),
              userContext: { userId: user?.id }
            }
          );

          const aiMessage = {
            id: `ai-${Date.now()}`,
            role: 'assistant',
            content: aiResponse?.data?.answer || aiResponse?.answer || 'I apologize, but I encountered an issue generating a response. Please try again.',
            created_at: new Date()?.toISOString(),
            message_type: 'text',
            metadata: aiResponse?.data?.metadata || {}
          };

          setMessages(prev => [...prev, aiMessage]);

          await supabase?.from('ai_chat_messages')?.insert({
            conversation_id: activeConversation?.id,
            user_id: user?.id,
            role: 'assistant',
            message_type: 'text',
            content: aiMessage?.content,
            metadata: aiMessage?.metadata
          });

          await loadSuggestions();
          setFailedMessage(null);
          setRetryCount(0);
          return; // success — exit
        } catch (err) {
          lastError = err;
          const errInfo = getGeminiErrorMessage(err);

          // Don't retry auth errors or if this is the last attempt
          if (!errInfo?.retryable || attempt === MAX_RETRIES) break;

          // Wait before next retry (exponential backoff)
          await sleep(RETRY_DELAY_MS * attempt);
        }
      }

      // All attempts failed
      throw lastError;

    } catch (error) {
      console.error('Failed to send message after retries:', error);

      const errInfo = getGeminiErrorMessage(error);
      if (errInfo?.isAuth) setApiKeyError(true);

      // Store the failed message so user can retry manually
      setFailedMessage({ text: messageText, type: messageType });
      setRetryCount(0);

      setMessages(prev => [...prev, {
        id: `error-${Date.now()}`,
        role: 'error',
        content: errInfo?.text,
        created_at: new Date()?.toISOString(),
        message_type: 'error',
        retryable: errInfo?.retryable,
        originalMessage: messageText,
        originalType: messageType,
      }]);

    } finally {
      setIsLoading(false);
      setIsStreaming(false);
      setStreamingMessage('');
    }
  };

  const handleRetry = (originalMessage, originalType) => {
    // Remove the last error message before retrying
    setMessages(prev => prev?.filter(m => m?.role !== 'error' || m?.originalMessage !== originalMessage));
    sendMessage(originalMessage, originalType, true);
  };

  const handleQuickReply = async (suggestionText) => {
    // Handle demo mode actions
    if (isDemoMode) {
      if (suggestionText === 'Sign in to access AI chat') {
        navigate('/login?redirect=/ai-chat-assistant');
        return;
      } else if (suggestionText === 'Create free account') {
        navigate('/login?redirect=/ai-chat-assistant');
        return;
      } else if (suggestionText === 'Learn about AI features') {
        const infoMessage = {
          id: `demo-info-${Date.now()}`,
          role: 'system',
          content: '🤖 **GrowGuardian AI Features:**\n\n✅ **Real-time IPM Guidance** - Expert advice on pest management\n✅ **Pest Identification** - Upload images for instant pest detection\n✅ **Treatment Recommendations** - Personalized treatment plans\n✅ **Conversation History** - Access your past chats anytime\n✅ **Smart Suggestions** - Context-aware quick replies\n\nSign in now to unlock all features!',
          created_at: new Date()?.toISOString(),
          message_type: 'system_notification'
        };
        setMessages(prev => [...prev, infoMessage]);
        return;
      }
    }
    
    // Check if it's the sign-in suggestion in authenticated mode
    if (suggestionText === 'Sign in for full access') {
      navigate('/login?redirect=/ai-chat-assistant');
      return;
    }
    
    await sendMessage(suggestionText);
  };

  const handleImageUpload = async (file, analysisText) => {
    const imageMessage = `I've uploaded an image for pest identification. ${analysisText || 'Please analyze this image and provide IPM recommendations.'}`;
    await sendMessage(imageMessage, 'image');
    setShowImageUpload(false);
  };

  const toggleBookmark = async (messageId) => {
    try {
      await aiChatService?.toggleBookmark(messageId, user?.id);
      await loadMessages(); // Refresh to show updated bookmark status
    } catch (error) {
      console.error('Failed to toggle bookmark:', error);
    }
  };

  const archiveConversation = async (conversationId) => {
    try {
      await aiChatService?.archiveConversation(conversationId, user?.id);
      await loadConversations();
      
      // Select first remaining conversation or create new one
      const remaining = conversations?.filter(c => c?.id !== conversationId);
      if (remaining?.length > 0) {
        setActiveConversation(remaining?.[0]);
      } else {
        await createNewConversation();
      }
    } catch (error) {
      console.error('Failed to archive conversation:', error);
    }
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">AI Chat Assistant</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-2">Get instant IPM guidance and support</p>
        </div>

        {/* Chat Layout - Responsive */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-6 h-[calc(100vh-12rem)] sm:h-[calc(100vh-14rem)]">
          {/* Conversation Sidebar - Collapsible on mobile */}
          <div className="lg:col-span-1 order-2 lg:order-1 max-h-64 lg:max-h-full overflow-y-auto">
            <ConversationSidebar
              conversations={conversations}
              activeConversation={activeConversation}
              onSelectConversation={setActiveConversation}
              onNewConversation={createNewConversation}
              onArchiveConversation={archiveConversation}
              collapsed={sidebarCollapsed}
              onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
            />
          </div>
          
          {/* Chat Window - Full width on mobile */}
          <div className="lg:col-span-3 order-1 lg:order-2 flex flex-col">
            <div className="flex-1 flex flex-col min-h-0">
              {/* Chat Header with Back Button - Mobile optimized */}
              <div className="bg-green-50 border-b px-3 py-3 sm:px-6 sm:py-4 flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                  {/* Back to Home Button */}
                  <button
                    onClick={() => navigate('/')}
                    className="p-1.5 sm:p-2 text-gray-500 hover:text-green-600 hover:bg-white rounded-lg transition-colors flex-shrink-0"
                    title="Back to Homepage"
                  >
                    <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                  <div className="hidden sm:block border-l border-gray-300 h-6 mx-2 flex-shrink-0"></div>
                  <div className="min-w-0 flex-1">
                    <h2 className="text-base sm:text-xl font-semibold text-gray-900 truncate">
                      {isDemoMode ? 'IPM Chat Assistant (Demo)' : (activeConversation?.title || 'IPM Chat Assistant')}
                    </h2>
                    <p className="text-xs sm:text-sm text-gray-600 truncate">
                      {isDemoMode ? 'Try it out • Sign in for full features' : 'Expert IPM guidance powered by AI'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                  <button
                    onClick={() => navigate('/')}
                    className="p-1.5 sm:p-2 text-gray-500 hover:text-green-600 transition-colors"
                    title="Exit to Homepage"
                  >
                    <Home className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                  {!isDemoMode && (
                    <button
                      onClick={() => setShowImageUpload(true)}
                      className="p-1.5 sm:p-2 text-gray-500 hover:text-green-600 transition-colors"
                      title="Upload image for pest identification"
                    >
                      <Upload className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                  )}
                  <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="p-1.5 sm:p-2 text-gray-500 hover:text-green-600 transition-colors"
                    title={isExpanded ? 'Collapse chat' : 'Expand chat'}
                  >
                    {isExpanded ? (
                      <Minimize2 className="w-4 h-4 sm:w-5 sm:h-5" />
                    ) : (
                      <Maximize2 className="w-4 h-4 sm:w-5 sm:h-5" />
                    )}
                  </button>
                </div>
              </div>

              {/* Messages Container - Mobile optimized scrolling */}
              <div className="flex-1 overflow-y-auto p-3 sm:p-6 space-y-3 sm:space-y-4 min-h-0">
                {!activeConversation && !isDemoMode ? (
                  <div className="flex items-center justify-center h-full text-gray-500">
                    <div className="text-center px-4">
                      <MessageCircle className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-4 text-gray-300" />
                      <p className="text-sm sm:text-base">Select a conversation or create a new one to start chatting</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <ChatWindow
                      messages={messages}
                      streamingMessage={streamingMessage}
                      isStreaming={isStreaming}
                      onToggleBookmark={!isDemoMode ? toggleBookmark : null}
                      onRetry={handleRetry}
                    />
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>

              {/* Quick Replies - Mobile optimized */}
              {suggestions?.length > 0 && (activeConversation || isDemoMode) && (
                <div className="px-3 py-2 sm:px-6 border-t bg-gray-50 flex-shrink-0">
                  <QuickReplies
                    suggestions={suggestions}
                    onSelectSuggestion={handleQuickReply}
                  />
                </div>
              )}

              {/* Message Input - Mobile optimized */}
              <div className="border-t bg-white p-3 sm:p-4 flex-shrink-0">
                <div className="flex items-end gap-2 sm:gap-3">
                  <div className="flex-1 min-w-0">
                    <textarea
                      ref={textareaRef}
                      value={newMessage}
                      onChange={(e) => setNewMessage(e?.target?.value)}
                      onKeyPress={handleKeyPress}
                      placeholder={isDemoMode ? "Try asking about pest identification (demo mode)..." : "Ask about pest identification, treatment recommendations..."}
                      className="w-full px-3 py-2 sm:px-4 sm:py-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm sm:text-base"
                      rows="2"
                      maxLength="1000"
                      disabled={isLoading || (!activeConversation && !isDemoMode)}
                    />
                  </div>
                  <button
                    onClick={() => sendMessage()}
                    disabled={!newMessage?.trim() || isLoading || (!activeConversation && !isDemoMode)}
                    className="bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white p-2.5 sm:p-3 rounded-lg transition-colors disabled:cursor-not-allowed flex-shrink-0"
                    title="Send message"
                  >
                    {isLoading ? (
                      <Loader className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 sm:w-5 sm:h-5" />
                    )}
                  </button>
                </div>
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mt-2 gap-1 sm:gap-0">
                  <span className="text-xs text-gray-500">
                    {newMessage?.length}/1000 characters
                  </span>
                  <span className="text-xs text-gray-500 hidden sm:block">
                    Press Enter to send • Shift+Enter for new line
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Image Upload Modal - Only in authenticated mode */}
      {showImageUpload && !isDemoMode && (
        <ImageUpload
          onUpload={handleImageUpload}
          onClose={() => setShowImageUpload(false)}
        />
      )}
    </div>
  );
}